// src/pages/SuperAdminDashboard.jsx
// Overview (package cards, module adoption, 12-month trend, onboarding totals, activity timeline),
// client management, business cards, PayNow / WhatsApp config, tickets and AI usage.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSuperAdminAuth } from '../contexts/SuperAdminAuthContext';
import { getAllOrganisations, getOpenTicketCount } from '../api/superAdminApi';
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid,
} from 'recharts';
import ClientSetupForm from '../components/superadmin/ClientSetupForm';
import ClientList from '../components/superadmin/ClientList';
import BusinessCardsList from '../components/superadmin/BusinessCardsList';
import PayNowConfiguration from '../components/superadmin/PayNowConfiguration';
import WhatsAppConfiguration from '../components/superadmin/WhatsAppConfiguration';
import TicketsList from '../components/superadmin/TicketsList';
import AIUsageTab from '../components/superadmin/AIUsageTab';
import { MODULE_LABELS, TOGGLEABLE_MODULE_KEYS } from '../components/superadmin/moduleMeta';
import '../styles/superadmin/sa-base.css';
import '../styles/superadmin/sa-header.css';
import '../styles/superadmin/sa-businesscards.css';
import '../styles/superadmin/sa-paynow.css';
import '../styles/superadmin/sa-whatsapp.css';
import '../styles/superadmin/sa-tickets.css';

//
const TAB_ICONS = {
  overview:      '📊',
  clients:       '👥',
  businesscards: '🪪',
  paynow:        '💳',
  whatsapp:      '📱',
  tickets:       '🎫',
  aiusage:       '🤖',
};

//
const EVENT_META = {
  client_onboarded:  { icon: '✦', label: 'Client Onboarded',      color: '#E64C31' },
  module_enabled:    { icon: '⬡', label: 'Module Enabled',         color: '#22C55E' },
  module_disabled:   { icon: '⬡', label: 'Module Disabled',        color: '#F59E0B' },
  ticket_opened:     { icon: '🎫', label: 'Support Ticket Opened',  color: '#3B82F6' },
  paynow_configured: { icon: '💳', label: 'PayNow Configured',      color: '#8B5CF6' },
};

function SuperAdminDashboard() {
  const { currentSuperAdmin, logout } = useSuperAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab,       setActiveTab]       = useState(location.state?.activeTab || 'overview');
  const [clients,         setClients]         = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState('');
  const [showCreateForm,  setShowCreateForm]  = useState(false);
  const [openTicketCount, setOpenTicketCount] = useState(0);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  useEffect(() => {
    loadClients();
    loadOpenTicketCount();
  }, []);

  useEffect(() => {
    if (location.state?.activeTab) setActiveTab(location.state.activeTab);
  }, [location.state]);

  useEffect(() => {
    if (!showProfileMenu) return;
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const data = await getAllOrganisations();
      setClients(data);
    } catch (err) {
      setError('Failed to load client configurations');
      console.error('Error loading clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadOpenTicketCount = async () => {
    // getOpenTicketCount swallows errors and returns 0, so the bell just shows no badge
    setOpenTicketCount(await getOpenTicketCount());
  };

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/superadmin/login');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  const handleClientCreated = () => { setShowCreateForm(false); loadClients(); };

  // ── derived stats ────────────────────────────────────────────
  const getPackageStats = () => {
    const aiEnabled = clients.filter(c => c.enabledModules?.bespokeai === true).length;
    return { aiEnabled, standard: clients.length - aiEnabled };
  };

  const getCreatedAt = (c) =>
    c.createdAt?.seconds ? new Date(c.createdAt.seconds * 1000) : new Date(c.createdAt);

  const getOnboardingStats = () => {
    const now            = new Date();
    const startOfWeek    = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
    const startOfMonth   = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfQuarter = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    const startOfYear    = new Date(now.getFullYear(), 0, 1);
    return {
      thisWeek:    clients.filter(c => getCreatedAt(c) >= startOfWeek).length,
      thisMonth:   clients.filter(c => getCreatedAt(c) >= startOfMonth).length,
      thisQuarter: clients.filter(c => getCreatedAt(c) >= startOfQuarter).length,
      thisYear:    clients.filter(c => getCreatedAt(c) >= startOfYear).length,
      allTime:     clients.length,
    };
  };

  //
  const getMonthlyTrend = () => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const offset = 11 - i;
      const mStart = new Date(now.getFullYear(), now.getMonth() - offset, 1);
      const mEnd   = new Date(now.getFullYear(), now.getMonth() - offset + 1, 0, 23, 59, 59);
      return {
        month:   mStart.toLocaleDateString('en-SG', { month: 'short', year: '2-digit' }),
        clients: clients.filter(c => { const d = getCreatedAt(c); return d >= mStart && d <= mEnd; }).length,
      };
    });
  };
  //

  //
  const getRelativeTime = (date) => {
    const diff  = Date.now() - date.getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins  < 1)  return 'just now';
    if (mins  < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days  < 7)  return `${days}d ago`;
    return date.toLocaleDateString('en-SG', { day: '2-digit', month: 'short' });
  };

  const getTimelineEvents = () =>
    clients
      .map(c => ({
        type:      'client_onboarded',
        label:     c.name || 'Unknown Client',
        sub:       `Org ID: ${c.orgId || c.id}`,
        timestamp: getCreatedAt(c),
      }))
      .filter(e => !isNaN(e.timestamp.getTime()))
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 10);
  //

  // ── computed ─────────────────────────────────────────────────
  const packageStats    = getPackageStats();
  const onboardingStats = getOnboardingStats();
  const monthlyTrend    = getMonthlyTrend();
  const timelineEvents  = getTimelineEvents();
  const totalClients    = clients.length || 1;

  const moduleChartData = TOGGLEABLE_MODULE_KEYS
    .filter(k => k !== 'bespokeai')
    .map(k => {
      const count = clients.filter(c => c.enabledModules?.[k]).length;
      return { name: MODULE_LABELS[k], count, pct: Math.round((count / totalClients) * 100) };
    });

  return (
    <div className="sa-portal">
    <div className="superadmin-dashboard">

      {/* ── Header — matches Admin Portal design ── */}
      <div className="admin-header-logo">
        <div className="admin-header-logo-content">

          {/* Left: BespokeBridge wordmark */}
          <div className="admin-header-logo-text">
            <h2>BespokeBridge</h2>
            <small>SuperAdmin</small>
          </div>

          {/* Centre: portal title */}
          <div className="dashboard-title-with-links">
            <h1>SuperAdmin Portal</h1>
          </div>

          {/* Right zone: quick-action pill + notification bell + profile */}
          <div className="header-right-zone">

            {/* Quick-action pill: Create Client */}
            <button
              className="header-quick-action-btn"
              onClick={() => { setActiveTab('clients'); setShowCreateForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              title="Create a new client"
            >
              Create Client
            </button>

            {/* Notification bell — open support tickets */}
            <button
              className="header-notification-btn"
              onClick={() => setActiveTab('tickets')}
              title={openTicketCount > 0 ? `${openTicketCount} open support ticket${openTicketCount !== 1 ? 's' : ''}` : 'No open tickets'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
              {openTicketCount > 0 && (
                <span className="header-notification-badge">
                  {openTicketCount > 99 ? '99+' : openTicketCount}
                </span>
              )}
            </button>

            {/* Profile avatar + dropdown */}
            <div className="header-profile-wrapper" ref={profileMenuRef}>
              <button
                className="header-profile-btn"
                onClick={() => setShowProfileMenu(prev => !prev)}
                title="SuperAdmin profile"
              >
                {currentSuperAdmin?.email ? currentSuperAdmin.email[0].toUpperCase() : 'S'}
              </button>
              {showProfileMenu && (
                <div className="header-profile-menu">
                  <div className="header-profile-menu-header">
                    <p className="header-profile-menu-name">
                      {currentSuperAdmin?.name || 'SuperAdmin'}
                    </p>
                    <p className="header-profile-menu-email">
                      {currentSuperAdmin?.email || ''}
                    </p>
                  </div>
                  <div className="header-profile-menu-divider" />
                  <button
                    className="header-profile-menu-item danger"
                    onClick={() => { setShowProfileMenu(false); handleSignOut(); }}
                  >
                    ↪ Sign Out
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* ── Nav tabs with icons ── */}
      <nav className="superadmin-nav">
        <div className="nav-content">
          {[
            { id: 'overview',      label: 'Overview' },
            { id: 'clients',       label: 'Client Management' },
            { id: 'businesscards', label: 'Business Cards' },
            { id: 'paynow',        label: 'PayNow Config' },
            { id: 'whatsapp',      label: 'WhatsApp Config' },
            { id: 'tickets',       label: 'Tickets' },
            { id: 'aiusage',       label: 'AI Usage' },
          ].map(tab => (
            <button
              key={tab.id}
              className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => { setActiveTab(tab.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              <span className="nav-tab-icon" aria-hidden="true">{TAB_ICONS[tab.id]}</span>
              {tab.label}
              {tab.id === 'tickets' && openTicketCount > 0 && (
                <span className="nav-tab-badge">{openTicketCount}</span>
              )}
            </button>
          ))}
        </div>
      </nav>

      <main className="superadmin-main">

        {/* ══════════ OVERVIEW ══════════ */}
        {activeTab === 'overview' && (
          <div className="overview-content">

            <section className="sa-section">
              <h2 className="sa-section-title">Package Statistics</h2>
              <div className="sa-package-cards">
                <div className="sa-package-card">
                  <div className="sa-package-card-accent" />
                  <div className="sa-package-card-body">
                    <span className="sa-package-card-number">{packageStats.standard}</span>
                    <span className="sa-package-card-label">Standard Package</span>
                    <span className="sa-package-card-sub">Core + optional modules</span>
                  </div>
                </div>
                <div className="sa-package-card sa-package-card--ai">
                  <div className="sa-package-card-accent" />
                  <div className="sa-package-card-body">
                    <span className="sa-package-card-number">{packageStats.aiEnabled}</span>
                    <span className="sa-package-card-label">AI-Enabled Package</span>
                    <span className="sa-package-card-sub">Includes BespokeAI module</span>
                  </div>
                </div>
                <div className="sa-package-card sa-package-card--total">
                  <div className="sa-package-card-accent" />
                  <div className="sa-package-card-body">
                    <span className="sa-package-card-number">{clients.length}</span>
                    <span className="sa-package-card-label">Total Clients</span>
                    <span className="sa-package-card-sub">All time</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Module Adoption cards */}
            <section className="sa-section">
              <h2 className="sa-section-title">Module Adoption</h2>
              <p className="sa-section-sub">Number of clients with each module enabled</p>
              <div className="sa-package-cards">
                {moduleChartData.map(m => (
                  <div className="sa-package-card" key={m.name}>
                    <div className="sa-package-card-accent" />
                    <div className="sa-package-card-body">
                      <span className="sa-package-card-number">{m.count}</span>
                      <span className="sa-package-card-label">{m.name}</span>
                      <span className="sa-package-card-sub">{m.pct}% of clients</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="sa-section">
              <h2 className="sa-section-title">New Clients — Last 12 Months</h2>
              <div className="sa-chart-card">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={monthlyTrend} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="clients"
                      name="New Clients"
                      stroke="#E64C31"
                      strokeWidth={2.5}
                      dot={{ fill: '#E64C31', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>

            {/* Onboarding period totals — compact stat row */}
            <section className="sa-section">
              <h2 className="sa-section-title">Onboarding Totals</h2>
              <div className="sa-onboarding-row">
                {[
                  { label: 'This Week',    value: onboardingStats.thisWeek },
                  { label: 'This Month',   value: onboardingStats.thisMonth },
                  { label: 'This Quarter', value: onboardingStats.thisQuarter },
                  { label: 'This Year',    value: onboardingStats.thisYear },
                  { label: 'All Time',     value: onboardingStats.allTime },
                ].map(stat => (
                  <div key={stat.label} className="sa-onboarding-stat">
                    <span className="sa-onboarding-number">{stat.value}</span>
                    <span className="sa-onboarding-label">{stat.label}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="sa-section">
              <h2 className="sa-section-title">Recent Activity</h2>
              <div className="sa-timeline">
                {timelineEvents.length === 0 && (
                  <p className="sa-timeline-empty">No recent activity to display.</p>
                )}
                {timelineEvents.map((event, i) => {
                  const meta = EVENT_META[event.type] || EVENT_META.client_onboarded;
                  return (
                    <div key={i} className="sa-timeline-item">
                      <div className="sa-timeline-spine">
                        <div
                          className="sa-timeline-dot"
                          style={{ borderColor: meta.color, color: meta.color }}
                        >
                          {meta.icon}
                        </div>
                        {i < timelineEvents.length - 1 && <div className="sa-timeline-line" />}
                      </div>
                      <div className="sa-timeline-content">
                        <span className="sa-timeline-event-type">{meta.label}</span>
                        <span className="sa-timeline-client">{event.label}</span>
                        {event.sub && <span className="sa-timeline-sub">{event.sub}</span>}
                        <span className="sa-timeline-time">{getRelativeTime(event.timestamp)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

          </div>
        )}

        {/* ══════════ CLIENTS ══════════ */}
        {activeTab === 'clients' && (
          <div className="clients-content">
            <div className="clients-header">
              <h2>Client Management</h2>
            </div>

            {error && <div className="error-message">{error}</div>}

            {showCreateForm && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <ClientSetupForm
                    onClientCreated={handleClientCreated}
                    onCancel={() => setShowCreateForm(false)}
                  />
                </div>
              </div>
            )}

            {loading ? (
              <div className="loading-spinner">Loading clients...</div>
            ) : (
              <ClientList
                clients={clients}
                onCreateClient={() => setShowCreateForm(true)}
              />
            )}
          </div>
        )}

        {activeTab === 'businesscards' && <BusinessCardsList />}
        {activeTab === 'paynow'        && <PayNowConfiguration />}
        {activeTab === 'whatsapp'      && <WhatsAppConfiguration />}
        {activeTab === 'tickets'       && <TicketsList />}
        {activeTab === 'aiusage'       && <AIUsageTab clients={clients} />}

      </main>
    </div>
    </div>
  );
}

export default SuperAdminDashboard;
